/**
 * Stale-chunk recovery banner.
 *
 * Vite emits a `vite:preloadError` event on `window` whenever a dynamic
 * import fails to fetch its chunk — typically because a deploy went out
 * mid-session, the user's open tab still references the old chunk hash,
 * and the new build no longer contains that filename. With Cloudflare's
 * SPA fallback turned OFF (see wrangler.jsonc), those requests now 404
 * cleanly instead of serving index.html with `Content-Type: text/html`,
 * which is what made the underlying problem so hard to spot — Chrome
 * surfaced it as "Failed to fetch dynamically imported module" and
 * Safari as "'text/html' is not a valid JavaScript MIME type".
 *
 * We don't auto-reload: this app holds blob photos and embeddings in
 * React state that the user hasn't exported yet. Forcing a reload would
 * silently throw that work away. Instead we render a fixed banner the
 * user can tap when they're ready to lose state, with a clear "update
 * available" framing so they understand why.
 *
 * The banner is plain DOM (not React) for two reasons:
 *   1. The error can fire at any time, including before React mounts or
 *      during a render cycle that itself depends on the failed chunk.
 *   2. Touching the React tree from an error handler invites secondary
 *      failures; a single <div> appended to <body> is bullet-proof.
 */

let installed = false;

export function installPreloadErrorBanner(): void {
  if (installed) return;
  installed = true;

  let banner: HTMLDivElement | null = null;

  const show = () => {
    if (banner) return;
    banner = document.createElement("div");
    banner.setAttribute("role", "alert");
    banner.style.cssText = [
      "position:fixed",
      "top:0",
      "left:0",
      "right:0",
      "z-index:2147483647",
      "padding:12px 16px",
      "background:#c8102e",
      "color:#fff",
      "font:600 14px/1.4 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
      "display:flex",
      "align-items:center",
      "justify-content:space-between",
      "gap:12px",
      "box-shadow:0 2px 8px rgba(0,0,0,0.2)",
    ].join(";");

    const text = document.createElement("span");
    text.textContent =
      "Field Capture was updated. Reload to continue (unsaved photos will be lost).";
    text.style.flex = "1";

    const button = document.createElement("button");
    button.type = "button";
    button.textContent = "Reload";
    button.style.cssText = [
      "appearance:none",
      "border:1px solid #fff",
      "background:#fff",
      "color:#c8102e",
      "border-radius:6px",
      "padding:8px 14px",
      "font:inherit",
      "cursor:pointer",
      "min-height:36px",
    ].join(";");
    button.addEventListener("click", () => window.location.reload());

    banner.append(text, button);
    (document.body ?? document.documentElement).append(banner);
  };

  // Vite-emitted event for failed dynamic imports. Payload includes the
  // module URL, but we don't surface it — the user doesn't care which
  // chunk, only that the app needs to refresh.
  window.addEventListener("vite:preloadError", () => show());

  // Belt-and-braces: catch the bare promise rejection in case Vite's
  // event ever stops firing (it has changed shape across Vite majors).
  // The string match is intentionally loose — Chrome and Safari phrase
  // this differently, and we'd rather false-positive a banner than miss
  // the real failure.
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : "";
    if (
      /failed to fetch dynamically imported module/i.test(message) ||
      /is not a valid javascript mime type/i.test(message)
    ) {
      show();
    }
  });
}
